/**
 * TopLocations Component
 * SEO-friendly section displaying top locations for the current country
 * Links to search results filtered by location
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TopLocations = () => {
  const { country, countryConfig } = useCountry();
  const [locationsData, setLocationsData] = useState({
    enabled: false,
    title: 'Top Locations',
    locations: []
  });
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/localization/public/top-locations/${country}`);
      if (response.data.success) {
        setLocationsData(response.data.top_locations);
      }
    } catch (err) {
      console.log('Could not fetch top locations');
      setLocationsData({ enabled: false, title: 'Top Locations', locations: [] });
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Don't render if disabled or no locations
  if (loading || !locationsData.enabled || locationsData.locations.length === 0) {
    return null;
  }

  return (
    <section 
      className="bg-gray-100 border-t border-gray-200 py-8"
      data-testid="top-locations-section"
      aria-label={`${locationsData.title} in ${countryConfig?.name || country}`}
    >
      <div className="max-w-7xl mx-auto px-4 text-center">
        {/* Section Header */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <MapPin className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-800">
            {locationsData.title}
          </h2>
        </div>
        
        {/* Locations Grid - SEO friendly with links */}
        <nav aria-label="Browse by location">
          <ul className="flex flex-wrap justify-center gap-x-1 gap-y-2">
            {locationsData.locations.map((location, index) => (
              <li key={location}>
                <Link
                  to={`/search?location=${encodeURIComponent(location)}&country=${country}`}
                  className="text-sm text-gray-600 hover:text-blue-600 hover:underline transition-colors"
                  title={`Browse listings in ${location}`}
                >
                  {location}
                </Link>
                {index < locationsData.locations.length - 1 && (
                  <span className="text-gray-400 ml-1">|</span>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
};

export default TopLocations;
